"use client";

import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  MessageSquare,
  Plus,
  RefreshCw,
  ShieldAlert,
  Trash2,
  X,
  Pencil,
  RotateCw,
  CheckCircle2,
  XCircle,
  Link as LinkIcon,
  UserCheck,
  Users,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import clsx from "clsx";
import { AdminShell } from "@/components/admin-shell";
import { FgCard } from "@/components/fg-card";
import { FgButton } from "@/components/fg-button";
import { FgInput } from "@/components/fg-input";
import { FgToggle } from "@/components/fg-toggle";
import { FgToast } from "@/components/fg-toast";
import { FgDialog } from "@/components/fg-dialog";
import { api } from "@/lib/api";

/* ────────────────────────────────────────────────────────────────────────── */

interface Level {
  id: string;
  name: string;
  displayName: string;
}

interface WhatsappGroup {
  id: string;
  levelId: string;
  groupName: string;
  inviteLink: string;
  isActive: boolean;
  lastRotatedAt: string | null;
  description: string | null;
  createdAt: string;
  joinCount: number;
}

interface WhatsappJoin {
  id: string;
  userId: string;
  levelId: string;
  source: "manual" | "link_tracking" | "admin";
  joinedAt: string;
}

type Tab = "groups" | "joins";

export function WhatsAppPage() {
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>("groups");
  const [editing, setEditing] = useState<WhatsappGroup | null>(null);
  const [creating, setCreating] = useState(false);
  const [rotating, setRotating] = useState<WhatsappGroup | null>(null);
  const [deleting, setDeleting] = useState<WhatsappGroup | null>(null);
  const [bulkConfirm, setBulkConfirm] = useState(false);

  const levels = useQuery<Level[]>({
    queryKey: ["levels"],
    queryFn: async () => (await api.get("/api/levels")).data,
  });

  const groups = useQuery<WhatsappGroup[]>({
    queryKey: ["admin", "whatsapp", "groups"],
    queryFn: async () => (await api.get("/api/admin/whatsapp/groups")).data,
  });

  const joins = useQuery<WhatsappJoin[]>({
    queryKey: ["admin", "whatsapp", "joins"],
    queryFn: async () => (await api.get("/api/admin/whatsapp/joins")).data,
  });

  const bulkRevoke = useMutation({
    mutationFn: async () =>
      (await api.post("/api/admin/whatsapp/groups/bulk-revoke")).data as { count: number },
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["admin", "whatsapp"] });
      FgToast.success(`Revoked ${res.count} group link${res.count === 1 ? "" : "s"}`);
      setBulkConfirm(false);
    },
    onError: (e) => FgToast.error((e as { message?: string }).message ?? "Bulk revoke failed"),
  });

  return (
    <AdminShell>
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-[rgba(255,255,255,0.08)] pb-6 mb-8"
      >
        <div>
          <div className="flex items-center gap-2 mb-1">
            <MessageSquare className="w-5 h-5 text-[#FFD60A]" />
            <span className="text-[11px] font-mono font-bold tracking-widest uppercase text-[#FFD60A]">
              WHATSAPP HOTLINE
            </span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-[#FAFAFA]">
            WhatsApp groups
          </h1>
          <p className="mt-1 text-sm text-[#8E8E93]">
            One WhatsApp group per level · paste the invite link, monitor joins.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {tab === "groups" && (
            <FgButton
              size="sm"
              variant="destructive"
              onClick={() => setBulkConfirm(true)}
              leftIcon={<ShieldAlert className="w-4 h-4" />}
            >
              Bulk-revoke all
            </FgButton>
          )}
          <FgButton
            size="sm"
            variant="primary"
            onClick={() => (tab === "groups" ? setCreating(true) : null)}
            leftIcon={<Plus className="w-4 h-4" />}
          >
            {tab === "groups" ? "Add group" : "Mark join"}
          </FgButton>
        </div>
      </motion.div>

      <div className="mb-6 inline-flex items-center gap-1 p-1 rounded-xl bg-[#1C1C1E]/60 border border-[rgba(255,255,255,0.08)] backdrop-blur">
        {(
          [
            { id: "groups" as const, label: "Groups", count: groups.data?.length },
            { id: "joins" as const, label: "Joins", count: joins.data?.length },
          ] as const
        ).map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={clsx(
              "relative px-3.5 py-1.5 rounded-lg text-xs font-bold capitalize transition-colors",
              tab === t.id ? "text-[#0A0A0A]" : "text-[#8E8E93] hover:text-[#FAFAFA]",
            )}
          >
            {tab === t.id && (
              <motion.div
                layoutId="whatsapp-tab"
                className="absolute inset-0 bg-[#FFD60A] rounded-lg"
                transition={{ type: "spring", stiffness: 500, damping: 30 }}
              />
            )}
            <span className="relative z-10">
              {t.label} {t.count !== undefined && `(${t.count})`}
            </span>
          </button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={tab}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.2 }}
        >
          {tab === "groups" && (
            <GroupsPanel
              groups={groups.data ?? []}
              levels={levels.data ?? []}
              loading={groups.isLoading}
              onEdit={setEditing}
              onRotate={setRotating}
              onDelete={setDeleting}
              onRefresh={() => groups.refetch()}
              onCreate={() => setCreating(true)}
            />
          )}
          {tab === "joins" && (
            <JoinsPanel
              joins={joins.data ?? []}
              levels={levels.data ?? []}
              loading={joins.isLoading}
              onRefresh={() => joins.refetch()}
            />
          )}
        </motion.div>
      </AnimatePresence>

      {(creating || editing) && (
        <GroupEditor
          group={editing}
          levels={levels.data ?? []}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ["admin", "whatsapp"] });
            setCreating(false);
            setEditing(null);
          }}
        />
      )}

      {rotating && (
        <RotateDialog
          group={rotating}
          onClose={() => setRotating(null)}
          onRotated={() => {
            qc.invalidateQueries({ queryKey: ["admin", "whatsapp"] });
            setRotating(null);
          }}
        />
      )}

      {deleting && (
        <FgDialog
          open
          tone="danger"
          title="Delete this WhatsApp group?"
          description={`Removes the group for "${deleting.groupName}" and revokes the invite link. Existing joins are not retroactively revoked.`}
          size="sm"
          onClose={() => setDeleting(null)}
          footer={
            <>
              <FgButton variant="ghost" onClick={() => setDeleting(null)}>
                Cancel
              </FgButton>
              <FgButton
                variant="primary"
                onClick={async () => {
                  try {
                    await api.delete(`/api/admin/whatsapp/groups/${deleting.id}`);
                    qc.invalidateQueries({ queryKey: ["admin", "whatsapp"] });
                    FgToast.success("Group removed");
                    setDeleting(null);
                  } catch (e) {
                    FgToast.error(
                      (e as { message?: string }).message ?? "Delete failed",
                    );
                  }
                }}
              >
                Delete group
              </FgButton>
            </>
          }
        />
      )}

      {bulkConfirm && (
        <FgDialog
          open
          tone="danger"
          title="Revoke every active WhatsApp invite link?"
          description="All groups will be deactivated in one shot. Users can no longer join from the stored links. Use this if a private group link has been leaked."
          size="sm"
          onClose={() => setBulkConfirm(false)}
          footer={
            <>
              <FgButton variant="ghost" onClick={() => setBulkConfirm(false)}>
                Cancel
              </FgButton>
              <FgButton
                variant="primary"
                onClick={() => bulkRevoke.mutate()}
                loading={bulkRevoke.isPending}
                leftIcon={<ShieldAlert size={14} />}
              >
                Revoke all
              </FgButton>
            </>
          }
        />
      )}
    </AdminShell>
  );
}

function GroupsPanel({
  groups,
  levels,
  loading,
  onEdit,
  onRotate,
  onDelete,
  onRefresh,
  onCreate,
}: {
  groups: WhatsappGroup[];
  levels: Level[];
  loading: boolean;
  onEdit: (g: WhatsappGroup) => void;
  onRotate: (g: WhatsappGroup) => void;
  onDelete: (g: WhatsappGroup) => void;
  onRefresh: () => void;
  onCreate: () => void;
}) {
  const levelById = useMemo(
    () => new Map(levels.map((l) => [l.id, l])),
    [levels],
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-xs text-[#8E8E93]">
          Each level has one WhatsApp group. Set the invite link, toggle
          active/inactive, or rotate the link if compromised.
        </p>
        <FgButton
          size="sm"
          variant="secondary"
          onClick={onRefresh}
          leftIcon={<RefreshCw className="w-4 h-4" />}
        >
          Refresh
        </FgButton>
      </div>

      {loading && (
        <div className="space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-20 rounded-xl bg-white/5 fg-skeleton" />
          ))}
        </div>
      )}

      {!loading && groups.length === 0 && (
        <FgCard className="p-10 text-center">
          <MessageSquare className="w-10 h-10 mx-auto text-[#8E8E93] mb-3" />
          <p className="text-sm text-[#FAFAFA] font-semibold">No WhatsApp groups yet</p>
          <p className="mt-1 text-xs text-[#8E8E93]">
            Add a group for a level — paste the invite link and learners can join.
          </p>
          <FgButton
            size="sm"
            className="mt-4"
            variant="primary"
            onClick={onCreate}
            leftIcon={<Plus className="w-4 h-4" />}
          >
            Create the first group
          </FgButton>
        </FgCard>
      )}

      {!loading && groups.length > 0 && (
        <div className="space-y-2">
          {groups.map((g, idx) => (
            <motion.div
              key={g.id}
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3, delay: idx * 0.04, ease: [0.16, 1, 0.3, 1] }}
            >
              <GroupCard
                group={g}
                levelName={levelById.get(g.levelId)?.displayName}
                levelCode={levelById.get(g.levelId)?.name}
                joinCount={g.joinCount}
                onEdit={onEdit}
                onRotate={onRotate}
                onDelete={onDelete}
              />
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}

function GroupCard({
  group,
  levelName,
  levelCode,
  joinCount,
  onEdit,
  onRotate,
  onDelete,
}: {
  group: WhatsappGroup;
  levelName?: string;
  levelCode?: string;
  joinCount: number;
  onEdit: (g: WhatsappGroup) => void;
  onRotate: (g: WhatsappGroup) => void;
  onDelete: (g: WhatsappGroup) => void;
}) {
  const lastRot = group.lastRotatedAt ? new Date(group.lastRotatedAt) : null;
  return (
    <FgCard className="p-4 group">
      <div className="flex items-start gap-4 flex-wrap">
        {levelCode && (
          <span className="font-mono text-[11px] font-bold bg-[#FFD60A] text-[#0A0A0A] px-2 py-0.5 rounded shrink-0">
            {levelCode}
          </span>
        )}
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-bold text-[#FAFAFA]">{group.groupName}</h3>
          <p className="text-[11px] text-[#8E8E93]">{levelName ?? "Unknown level"}</p>
          <div className="mt-2 flex items-center gap-2 flex-wrap text-[11px] font-mono">
            <span
              className={clsx(
                "inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5",
                group.isActive
                  ? "bg-[#34C759]/15 text-[#34C759] border-[#34C759]/30"
                  : "bg-[#8E8E93]/15 text-[#8E8E93] border-[#8E8E93]/30",
              )}
            >
              {group.isActive ? <CheckCircle2 size={11} /> : <XCircle size={11} />}
              {group.isActive ? "Active" : "Inactive"}
            </span>
            <span className="inline-flex items-center gap-1 rounded-full bg-white/5 px-1.5 py-0.5 text-[#8E8E93]">
              <Users size={11} /> {joinCount} joined
            </span>
            {lastRot && (
              <span className="inline-flex items-center gap-1 rounded-full bg-white/5 px-1.5 py-0.5 text-[#8E8E93]">
                <RotateCw size={11} /> rotated {lastRot.toLocaleDateString()}
              </span>
            )}
          </div>
          <a
            href={group.inviteLink}
            target="_blank"
            rel="noreferrer"
            className="mt-2 inline-flex items-center gap-1.5 text-[11px] text-[#FFD60A] hover:underline font-mono max-w-full truncate"
            title={group.inviteLink}
          >
            <LinkIcon size={11} /> {group.inviteLink}
          </a>
        </div>
        <div className="flex flex-col gap-1.5 ml-auto">
          <FgButton
            size="sm"
            variant="outline"
            onClick={() => onEdit(group)}
            leftIcon={<Pencil size={12} />}
          >
            Edit
          </FgButton>
          <FgButton
            size="sm"
            variant="secondary"
            onClick={() => onRotate(group)}
            leftIcon={<RotateCw size={12} />}
          >
            Rotate link
          </FgButton>
          <FgButton
            size="sm"
            variant="ghost"
            onClick={() => onDelete(group)}
            leftIcon={<Trash2 size={12} />}
          >
            Delete
          </FgButton>
        </div>
      </div>
    </FgCard>
  );
}

function JoinsPanel({
  joins,
  levels,
  loading,
  onRefresh,
}: {
  joins: WhatsappJoin[];
  levels: Level[];
  loading: boolean;
  onRefresh: () => void;
}) {
  const levelById = useMemo(
    () => new Map(levels.map((l) => [l.id, l])),
    [levels],
  );
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-xs text-[#8E8E93]">
          Every user-recorded join. We don't scrape WhatsApp; admins log these
          manually or via a future link-tracking webhook.
        </p>
        <FgButton
          size="sm"
          variant="secondary"
          onClick={onRefresh}
          leftIcon={<RefreshCw className="w-4 h-4" />}
        >
          Refresh
        </FgButton>
      </div>

      {loading && (
        <div className="space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-12 rounded-xl bg-white/5 fg-skeleton" />
          ))}
        </div>
      )}

      {!loading && joins.length === 0 && (
        <FgCard className="p-10 text-center">
          <UserCheck className="w-10 h-10 mx-auto text-[#8E8E93] mb-3" />
          <p className="text-sm text-[#FAFAFA] font-semibold">No joins yet</p>
          <p className="mt-1 text-xs text-[#8E8E93]">
            When a learner claims a WhatsApp invite, mark them joined here.
          </p>
        </FgCard>
      )}

      {!loading && joins.length > 0 && (
        <FgCard className="overflow-hidden">
          <div className="grid grid-cols-12 gap-2 px-4 py-2 text-[10px] font-mono font-bold uppercase tracking-widest text-[#8E8E93] border-b border-[rgba(255,255,255,0.06)]">
            <div className="col-span-4">User</div>
            <div className="col-span-3">Level</div>
            <div className="col-span-2">Source</div>
            <div className="col-span-3 text-right">Joined</div>
          </div>
          <div className="divide-y divide-[rgba(255,255,255,0.04)]">
            {joins.map((j, idx) => {
              const level = levelById.get(j.levelId);
              return (
                <motion.div
                  key={j.id}
                  initial={{ opacity: 0, x: -4 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.25, delay: Math.min(idx, 12) * 0.02 }}
                  className="grid grid-cols-12 gap-2 px-4 py-2.5 text-sm items-center"
                >
                  <div className="col-span-4 font-mono text-[11px] text-[#FAFAFA] truncate">
                    {j.userId}
                  </div>
                  <div className="col-span-3 text-[#FAFAFA] truncate">
                    {level?.name ?? j.levelId.slice(0, 8) + "…"}
                    <span className="text-[10px] text-[#8E8E93] ml-1">
                      {level?.displayName ?? ""}
                    </span>
                  </div>
                  <div className="col-span-2">
                    <span className="font-mono text-[10px] bg-white/5 px-1.5 py-0.5 rounded uppercase text-[#8E8E93]">
                      {j.source}
                    </span>
                  </div>
                  <div className="col-span-3 text-right font-mono text-[11px] text-[#8E8E93]">
                    {new Date(j.joinedAt).toLocaleString()}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </FgCard>
      )}
    </div>
  );
}

function GroupEditor({
  group,
  levels,
  onClose,
  onSaved,
}: {
  group: WhatsappGroup | null;
  levels: Level[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [levelId, setLevelId] = useState(group?.levelId ?? levels[0]?.id ?? "");
  const [groupName, setGroupName] = useState(group?.groupName ?? "");
  const [inviteLink, setInviteLink] = useState(group?.inviteLink ?? "");
  const [description, setDescription] = useState(group?.description ?? "");
  const [isActive, setIsActive] = useState(group?.isActive ?? true);

  useEffect(() => {
    if (group) {
      setLevelId(group.levelId);
      setGroupName(group.groupName);
      setInviteLink(group.inviteLink);
      setDescription(group.description ?? "");
      setIsActive(group.isActive);
    } else {
      setLevelId(levels[0]?.id ?? "");
      setGroupName("");
      setInviteLink("");
      setDescription("");
      setIsActive(true);
    }
  }, [group, levels]);

  const save = useMutation({
    mutationFn: async () => {
      const body = { levelId, groupName, inviteLink, description, isActive };
      return (await api.post("/api/admin/whatsapp/groups", body)).data;
    },
    onSuccess: () => {
      FgToast.success(group ? "Group updated" : "Group created");
      onSaved();
    },
    onError: (e) => FgToast.error((e as { message?: string }).message ?? "Save failed"),
  });

  return (
    <FgCard className="mt-6 p-5">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-bold text-[#FAFAFA]">
          {group ? "Edit WhatsApp group" : "Add WhatsApp group"}
        </h2>
        <button
          onClick={onClose}
          className="p-1.5 rounded-lg text-[#8E8E93] hover:text-[#FAFAFA] hover:bg-white/10 transition-colors"
        >
          <X size={16} />
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#8E8E93] mb-1.5">
            Level
          </label>
          <select
            value={levelId}
            onChange={(e) => setLevelId(e.target.value)}
            disabled={!!group}
            className="h-9 w-full rounded-lg border border-[rgba(255,255,255,0.1)] bg-[#1C1C1E] px-3 text-sm text-[#FAFAFA] focus:outline-none focus:border-[#FFD60A] disabled:opacity-50"
          >
            {levels.map((l) => (
              <option key={l.id} value={l.id}>
                {l.name} · {l.displayName}
              </option>
            ))}
          </select>
        </div>
        <FgInput
          label="Group name (display)"
          value={groupName}
          onChange={(e) => setGroupName(e.target.value)}
          placeholder="A1.1 — Absolute Beginner Chat"
          required
        />
      </div>
      <div className="mt-3">
        <FgInput
          label="Invite link (WhatsApp chat.whatsapp.com or wa.me/...)"
          value={inviteLink}
          onChange={(e) => setInviteLink(e.target.value)}
          placeholder="https://chat.whatsapp.com/..."
          required
        />
      </div>
      <div className="mt-3">
        <FgInput
          label="Description (optional)"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </div>
      <div className="mt-3">
        <FgToggle
          label="Group is active (link accepts new joins)"
          checked={isActive}
          onChange={setIsActive}
        />
      </div>
      <div className="mt-5 flex justify-end gap-2">
        <FgButton variant="ghost" onClick={onClose}>
          Cancel
        </FgButton>
        <FgButton
          variant="primary"
          onClick={() => save.mutate()}
          loading={save.isPending}
        >
          {group ? "Save" : "Create group"}
        </FgButton>
      </div>
    </FgCard>
  );
}

function RotateDialog({
  group,
  onClose,
  onRotated,
}: {
  group: WhatsappGroup;
  onClose: () => void;
  onRotated: () => void;
}) {
  const [newLink, setNewLink] = useState("");
  const rotate = useMutation({
    mutationFn: async () =>
      (await api.post(`/api/admin/whatsapp/groups/${group.id}/rotate`, { inviteLink: newLink })).data,
    onSuccess: () => {
      FgToast.success("Invite link rotated");
      onRotated();
    },
    onError: (e) => FgToast.error((e as { message?: string }).message ?? "Rotate failed"),
  });

  return (
    <FgCard className="mt-6 p-5">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-base font-bold text-[#FAFAFA]">
          Rotate link for <span className="text-[#FFD60A]">{group.groupName}</span>
        </h2>
        <button
          onClick={onClose}
          className="p-1.5 rounded-lg text-[#8E8E93] hover:text-[#FAFAFA] hover:bg-white/10 transition-colors"
        >
          <X size={16} />
        </button>
      </div>
      <p className="text-xs text-[#8E8E93] mb-3">
        The old invite link stops working the moment the new one lands. Use
        this when a private group link has been shared publicly by accident.
      </p>
      <FgInput
        label="New invite link"
        value={newLink}
        onChange={(e) => setNewLink(e.target.value)}
        placeholder="https://chat.whatsapp.com/..."
      />
      <div className="mt-4 flex justify-end gap-2">
        <FgButton variant="ghost" onClick={onClose}>
          Cancel
        </FgButton>
        <FgButton
          variant="primary"
          onClick={() => rotate.mutate()}
          loading={rotate.isPending}
          disabled={!newLink.startsWith("https://")}
          leftIcon={<RotateCw size={14} />}
        >
          Rotate link
        </FgButton>
      </div>
    </FgCard>
  );
}
